import { Box, Stack } from '@mui/material';
import {
  useGetGuestJourneyStageMovementCardsQuery,
  useGetGuestJourneyStageMovementDataQuery,
} from 'api/reports-service/guest-journey-hooks';
import SearchToolbar from 'components/search/search-toolbar/search-toolbar.component';
import {
  MultipleSearchQueryProvider,
  SearchParamsProvider,
} from 'components/search/search.context';
import {
  useSearchParam,
  useSearchParams,
  useSearchQuery,
} from 'components/search/search.hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  stageMovementFilteredSearchParams,
  useStageMovementSearchParams,
} from '../guest-journey.hooks';
import MovementSankey from '../movement/movement-sankey.component';
import { buildMovementSankey } from '../movement/movement-sankey.utils';
import ReportFieldsLink from '../report-fields/report-fields-link.component';
import MovementSummaryCards from '../movement/movement-summary-cards.component';
import MovementTable from '../movement/movement-table.component';

/**
 * Queries only send the params on this tab's filter bar (Locations + Date
 * Range + Stage).
 */
const filteredSearchParams = stageMovementFilteredSearchParams;

// Render order for the five KPI cards — titles/values come from the BE.
const cardOrder = [
  'currentTotalMembers',
  'incomingMembers',
  'outgoingMembers',
  'frequencyMedianDays',
  'totalSpendPerCustomer',
];

const SearchParamsWrapper = () => {
  const searchParams = useSearchParams(useStageMovementSearchParams());
  return (
    <SearchParamsProvider value={searchParams}>
      <StageMovement />
    </SearchParamsProvider>
  );
};

const StageMovement = () => {
  const { t } = useTranslation();

  const summary = useSearchQuery(useGetGuestJourneyStageMovementCardsQuery, {
    filteredSearchParams,
  });
  // One movement payload — the sankey and both tables render from it.
  const movement = useSearchQuery(useGetGuestJourneyStageMovementDataQuery, {
    filteredSearchParams,
  });

  const stageParam = useSearchParam<string>('stage');
  const selectedStageOption = stageParam?.options?.find(
    o => o.id === stageParam?.value,
  );
  const centerLabel = selectedStageOption?.label;

  // Stage names arrive display-ready from the BE (configured per client), so the
  // sankey and tables render them verbatim — no FE label resolution.
  const stayedLabel = t('page.guestJourney.movement.stayed');
  const sankeyGraph = useMemo(
    () =>
      movement.data &&
      buildMovementSankey(movement.data, {
        centerId: stageParam?.value ?? 'center',
        stayedLabel,
      }),
    [movement.data, stageParam?.value, stayedLabel],
  );

  return (
    <Stack marginTop={2} data-testid="GuestJourney.StageMovement">
      <MultipleSearchQueryProvider
        value={{
          queries: { summary, movement },
        }}
      >
        <Stack spacing={2}>
          <SearchToolbar />

          <ReportFieldsLink tab="stageMovement" />

          <MovementSummaryCards
            testIdPrefix="StageMovement"
            cardOrder={cardOrder}
            data={summary.data}
            loading={summary.queryState.isFetching}
          />

          <MovementSankey
            data={sankeyGraph}
            loading={movement.queryState.isFetching}
            centerLabel={centerLabel}
            data-testid="StageMovement.Sankey"
          />

          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }}
          >
            <MovementTable
              title={t('page.guestJourney.movement.incoming')}
              rows={movement.data?.incoming ?? []}
              loading={movement.queryState.isFetching}
              data-testid="StageMovement.Incoming"
            />
            <MovementTable
              title={t('page.guestJourney.movement.outgoing')}
              rows={movement.data?.outgoing ?? []}
              loading={movement.queryState.isFetching}
              data-testid="StageMovement.Outgoing"
            />
          </Box>
        </Stack>
      </MultipleSearchQueryProvider>
    </Stack>
  );
};

export default SearchParamsWrapper;
